import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { NAV, FOOTER_NAV, FOOTER_LEGAL, CONTACT } from '../data/site';

const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-slate-300">
      <div className="container-wide py-14 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <span className="text-[16px] font-extrabold tracking-tight text-white">
                metakaolin<span className="text-blue-400">.info</span>
              </span>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-md">{t('footer.tagline')}</p>
            <a
              href={CONTACT.website}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-400 hover:text-blue-300 transition-colors"
            >
              {t('common.visitNewchem')}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* Quick links */}
          <div className="md:col-span-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500">{t('footer.quickLinks')}</p>
            <ul className="mt-4 space-y-2.5">
              {[...NAV, ...FOOTER_NAV].map((item) => (
                <li key={item.key}>
                  <Link to={item.path} className="text-sm text-slate-400 hover:text-white transition-colors">
                    {t(`nav.${item.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-500">{t('footer.contactTitle')}</p>
            <p className="mt-4 text-sm font-semibold text-white">{CONTACT.company}</p>
            <ul className="mt-3 space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-blue-400 shrink-0" />
                <span>{CONTACT.street}, {CONTACT.city}, {CONTACT.country}</span>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-blue-400 shrink-0" />
                <a href={`mailto:${CONTACT.email}`} className="hover:text-white transition-colors">{CONTACT.email}</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-blue-400 shrink-0" />
                <a href={`tel:${CONTACT.phone.replace(/\s/g, '')}`} className="hover:text-white transition-colors">{CONTACT.phone}</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col gap-4">
          <nav className="flex flex-wrap items-center gap-x-6 gap-y-2" aria-label={t('footer.legal')} data-testid="footer-legal-nav">
            {FOOTER_LEGAL.map((item) => (
              <Link
                key={item.key}
                to={item.path}
                data-testid={`footer-legal-${item.key}`}
                className="text-[13px] font-semibold text-slate-300 hover:text-white transition-colors"
              >
                {t(`nav.${item.key}`)}
              </Link>
            ))}
          </nav>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[13px] text-slate-500">
              © {year} {CONTACT.company}. {t('footer.rights')}
            </p>
            <p className="text-[13px] text-slate-500">
              {t('footer.builtBy')}{' '}
              <a href={CONTACT.website} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-300 hover:text-white">
                Newchem GmbH
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
